import type { ReactNode } from "react";
import { Menu, PanelLeftOpen, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GlobalLoadingBar } from "@/components/GlobalLoadingBar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export function AppMobileBar({
  logoSrc, logoAlt, onOpenMenu, account,
}: {
  logoSrc: string;
  logoAlt: string;
  onOpenMenu: () => void;
  account: ReactNode;
}) {
  return (
    <div className="fixed top-0 left-0 right-0 z-40 h-12 bg-sidebar border-b border-sidebar-border flex items-center justify-between px-3 safe-area-top">
      <button onClick={onOpenMenu} className="p-1.5 rounded-md hover:bg-sidebar-accent/50">
        <Menu className="h-5 w-5 text-sidebar-foreground" />
      </button>
      <img src={logoSrc} alt={logoAlt} className="h-6 object-contain" />
      {account}
    </div>
  );
}

export function AppSidebar({
  isMobile, collapsed, mobileOpen, onMobileClose, onExpand, logoSrc, logoAlt, head, footer, children,
}: {
  isMobile: boolean;
  collapsed: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  onExpand: () => void;
  logoSrc: string;
  logoAlt: string;
  head?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 transition-opacity"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={cn(
          "flex flex-col transition-all duration-200",
          isMobile
            ? cn("fixed top-0 left-0 z-[70] w-[75vw] max-w-[260px] h-[100dvh] bg-sidebar shadow-2xl transform transition-transform duration-300 border-r border-sidebar-border", mobileOpen ? "translate-x-0" : "-translate-x-full")
            : cn("relative min-h-0", collapsed ? "w-14" : "w-[252px]"),
        )}
      >
        {isMobile && (
          <div className="flex items-center justify-between border-b border-sidebar-border px-3 py-1.5">
            <img src={logoSrc} alt={logoAlt} className="h-7 w-auto object-contain" />
            <button onClick={onMobileClose} className="p-1.5 rounded-md hover:bg-sidebar-accent/50">
              <X className="h-5 w-5 text-sidebar-foreground" />
            </button>
          </div>
        )}
        {head && <div className={cn("shrink-0", collapsed ? "px-2 pt-2" : "px-3 pt-2")}>{head}</div>}
        <nav className={cn("flex-1 overflow-y-auto px-3 pb-4 pt-2 space-y-1.5", !isMobile && collapsed && "scrollbar-none")}>
          {!isMobile && collapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onExpand}
                  aria-label="Expandir menú"
                  className="w-full flex items-center justify-center rounded-md p-2.5 text-ink-700 transition-colors hover:bg-black/[0.04] hover:text-ink-900 dark:hover:bg-white/[0.06]"
                >
                  <PanelLeftOpen className="h-5 w-5 flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Expandir menú</TooltipContent>
            </Tooltip>
          )}
          {children}
        </nav>
        {footer && <div className="shrink-0 px-3 pt-2">{footer}</div>}
        <div aria-hidden className={cn("shrink-0", footer && !isMobile ? "h-2" : "h-16")} />
      </aside>
    </>
  );
}

export function AppMain({ isMobile, children }: { isMobile: boolean; children: ReactNode }) {
  return (
    <main className={cn("app-panel relative flex-1 flex flex-col overflow-hidden", isMobile ? "pt-12" : "mb-2 mr-2")}>
      <div className={cn("pointer-events-none absolute inset-x-0 z-30", isMobile ? "top-12" : "top-0")}>
        <GlobalLoadingBar />
      </div>
      {children}
    </main>
  );
}
